"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import TextInput from "../../input/TextInput";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";

interface FullSliderWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const FullSliderWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: FullSliderWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            image: undefined,
            image_mobile: undefined,
            title: '',
            title_en: '',
            title_ar: '',
            description: '',
            description_en: '',
            description_ar: '',
            buttonContent: '',
            buttonContent_en: '',
            buttonContent_ar: '',
            link: '',
        });
    };

    const removeItem = (sliderIndex: number) => {
        if (fields.length <= 1) return;
        remove(sliderIndex);
    };

    return (
        <>
            {fields.map((field, sliderIndex) => {
                const imageError = getError(`${name}.${widgetIndex}.items.${sliderIndex}.image`) as string | undefined;
                const imageMobileError = getError(`${name}.${widgetIndex}.items.${sliderIndex}.image_mobile`) as string | undefined;

                return (
                    <div key={field.id} className="border col-span-2 p-4 rounded-md space-y-4 bg-background">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                اسلاید {sliderIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(sliderIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs text-primary-600">تصویر دسکتاپ</label>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${sliderIndex}.image`}
                                    render={({ field }) => (
                                        <FileUploadInput
                                            accept="image/*,video/*"
                                            disabled={disabled}
                                            onUploadSuccess={(image) => {
                                                if (!Array.isArray(image) && image) field.onChange(image);
                                            }}
                                            defaultImages={
                                                field.value
                                                    ? [{ ...field.value, name: field.value.name || '', description: field.value.description || '' }]
                                                    : []
                                            }
                                            onDefaultRemove={() => field.onChange(undefined)}
                                            error={imageError}
                                        />
                                    )}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs text-primary-600">تصویر موبایل</label>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${sliderIndex}.image_mobile`}
                                    render={({ field }) => (
                                        <FileUploadInput
                                            accept="image/*,video/*"
                                            disabled={disabled}
                                            onUploadSuccess={(image) => {
                                                if (!Array.isArray(image) && image) field.onChange(image);
                                            }}
                                            defaultImages={
                                                field.value
                                                    ? [{ ...field.value, name: field.value.name || '', description: field.value.description || '' }]
                                                    : []
                                            }
                                            onDefaultRemove={() => field.onChange(undefined)}
                                            error={imageMobileError}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${sliderIndex}.title`}
                            label={dictionary.forms?.title}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <MultilingualTextareaInput
                            baseName={`${name}.${widgetIndex}.items.${sliderIndex}.description`}
                            label={dictionary.forms?.description}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${sliderIndex}.buttonContent`}
                            label={dictionary.forms?.buttonContent}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <TextInput
                            name={`${name}.${widgetIndex}.items.${sliderIndex}.link`}
                            label={dictionary.forms?.link}
                            placeholder={dictionary.forms?.link}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />
                    </div>
                );
            })}

            <Button type="button" variant="outline" onClick={addItem} disabled={disabled} className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};